"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "CheckBadgeIcon",
    title: "Apply & Get Verified",
    description:
      "Submit your business application with basic info. We review within 1 business day and grant you a verified partner badge visible to all pet owners.",
    color: "#5EBFB3",
    bg: "#E8F7F6",
  },
  {
    number: "02",
    icon: "UserGroupIcon",
    title: "Connect With Clients",
    description:
      "Pet owners send you connection requests. You accept and instantly see the pet profiles they choose to share—medical history, allergies, preferences.",
    color: "#7C3AED",
    bg: "#EDE9FE",
  },
  {
    number: "03",
    icon: "BellIcon",
    title: "Automate & Grow",
    description:
      "Set up automated reminders for vaccine due dates, appointment rebooking, and follow-ups. Use loyalty tools and analytics to grow your business.",
    color: "#0369A1",
    bg: "#E0F2FE",
  },
];

export default function BusinessFlow() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24"
      style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 scroll-fade-up">
          <span className="teal-badge mb-4">For Businesses</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] mt-4 mb-5 tracking-tight">
            Serve clients better with instant access
          </h2>
          <p className="text-lg text-[#4A6363] max-w-2xl mx-auto leading-relaxed">
            Join as a verified partner and get tools to streamline client communication, automate reminders, and grow your pet business.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-10 relative">
          {/* Connector line (desktop) */}
          <div
            className="hidden md:block absolute top-[52px] left-[calc(16.67%+24px)] right-[calc(16.67%+24px)] h-[2px] z-0"
            style={{
              background: "linear-gradient(to right, #5EBFB3, #7C3AED, #0369A1)",
            }}
          />

          {steps.map((step, idx) => (
            <div
              key={step.number}
              className={`scroll-fade-up stagger-${idx + 1} relative z-10`}
            >
              <div className="feature-card p-7 h-full flex flex-col">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 relative animate-pulse-glow"
                  style={{ background: step.bg }}
                >
                  <Icon
                    name={step.icon as "CheckBadgeIcon"}
                    size={24}
                    style={{ color: step.color }}
                  />
                  <span
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-white text-[10px] font-extrabold font-heading flex items-center justify-center"
                    style={{ background: step.color }}
                  >
                    {idx + 1}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-[#1A2B2B] mb-3 font-heading leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#4A6363] leading-relaxed text-sm flex-1">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}